<template>
  <div class="create-page-wrapper">
    <form
      autocomplete="off"
      @submit.prevent="handleFormSubmit"
      v-if="!preloaderVisibility"
    >
      <div class="container">
        <div class="row">
          <div class="col-6">
            <h2>Name</h2>
            <label>
              <input
                type="text"
                name="first-name"
                placeholder="First Name"
                v-model="userInfo.firstName"
                :class="{ 'not-filled': highlightingData.firstName }"
              />
            </label>
            <label>
              <input
                type="text"
                name="last-name"
                placeholder="Last Name"
                v-model="userInfo.lastName"
                :class="{ 'not-filled': highlightingData.lastName }"
              />
            </label>
          </div>
          <div class="col-6">
            <h2>Authentication</h2>
            <label for="email">
              <input
                type="email"
                name="email"
                autocomplete="new-email"
                placeholder="User Email"
                :class="{ 'not-filled': highlightingData.email }"
                v-model="userInfo.email"
              />
            </label>
            <label for="password">
              <input
                id="password"
                name="password"
                type="password"
                autocomplete="new-password"
                placeholder="New Password"
                minlength="6"
                v-model="userInfo.password"
              />
            </label>

            <label for="confirm-password">
              <input
                id="confirm-password"
                type="password"
                placeholder="Confirm New Password"
                minlength="6"
                v-model="validateFields.passwordConfirm"
              />
            </label>
          </div>
          <div class="col-6">
            <h2>Birth Details</h2>
            <label>
              <input
                :max="new Date().toISOString().split('T')[0]"
                name="date"
                type="date"
                v-model="dateTimeFields.date"
                required="required"
              />
            </label>
            <label>
              <input
                type="time"
                name="time"
                v-model="dateTimeFields.time"
                required="required"
              />
            </label>
            <label class="vue-select" :class="{ isRequired: highlightingData.countryOfBirth }">
              <vueSelect
                required="required"
                v-model="userInfo.birthCountry"
                :value="countrySearch"
                @search="getSelectedCountry"
                :options="countriesList"
                :multipe="false"
                :closeOnSelect="true"
                placeholder="Country of Birth"
              />
            </label>
            <label class="vue-select" :class="{ isRequired: highlightingData.cityOfBirth }">
              <vueSelect
                :disabled="!userInfo.birthCountry"
                required="required"
                v-model="userInfo.birthCity"
                label="cityFullName"
                @search="getSelectedCity"
                :options="citiesList"
                :multiple="false"
                :closeOnSelect="true"
                placeholder="City of Birth"
              />
            </label>
          </div>

          <div class="col-6">
            <h2>Language</h2>
            <label class="vue-select">
              <vueSelect
                v-model="userInfo.language"
                :options="languageOptions"
                :reduce="option => option.value"
                label="label"
                placeholder="Select Language"
                :clearable="false"
              />
            </label>
            <h2>Sign Up Date</h2>
            <label>
              <input type="text" name="signUp" :value="signUpDate" disabled />
            </label>
            <h2>Subscription</h2>
            <label>
              <input type="text" value="Free" disabled />
            </label>
            <h2>Overwrite Access</h2>
            <label class="vue-select">
              <vueSelect
                class="subscription-select"
                v-model="forceSubscription"
                placeholder="Subscription"
                :cleareable="false"
                :options="['None', 'Force Premium']"
                :multiple="false"
                required="required"
              />
            </label>
            <label v-if="forceSubscription === 'Force Premium'">
              <input
                :min="new Date().toISOString().split('T')[0]"
                name="date"
                type="date"
                v-model="endOfAccess"
                required="required"
              />
            </label>
          </div>
        </div>
        <div class="row" v-if="errorMessage.length">
          <div class="col-6">
            <div class="errors-wrapper">
              <div v-for="(singleError, index) in errorMessage" :key="index">
                {{ singleError }}
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-6 align-end buttons-wrapper">
            <button @click.prevent="handleRedirect">Cancel</button>
            <input type="submit" value="Create" name="submit" />
          </div>
        </div>
      </div>
    </form>
    <PreloaderComponent v-if="preloaderVisibility" />
  </div>
</template>

<script>
import debounce from "debounce";
import vueSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import SolFileAPIInstance from "@/api";
import PreloaderComponent from "@/components/common/PreloaderComponent";

export default {
  name: "CreateUser",
  data() {
    return {
      awaitResponse: false,
      checkComplete: false,
      modalView: false,
      userInfo: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        birthCity: null,
        birthCountry: null,
        language: "en",
      },
      validateFields: {
        passwordConfirm: "",
      },
      dateTimeFields: {
        date: "",
        time: "",
      },
      endOfAccess: null,
      signUpDate: "",
      subscription: "",
      forceSubscription: "None",
      errorMessage: [],

      countrySearch: "",
      countriesList: [],

      citySearch: "",
      citiesList: [],

      languageOptions: [
        { label: 'English', value: 'en' },
        { label: 'German', value: 'de' }
      ],

      preloaderVisibility: true,

      highlightingData: {
        firstName: false,
        lastName: false,
        email: false,
        passwordConfirm: false,
        password: false,
        cityOfBirth: false,
        countryOfBirth: false,
        timeOfBirth: false,
        dateOfBirth: false,
      },
    };
  },
  components: {
    vueSelect,
    PreloaderComponent,
  },

  watch: {
    "userInfo.birthCountry"(value) {
      if (!value) {
        this.userInfo.birthCity = null;
      }
    },
  },
  mounted() {
    this.preloaderVisibility = false;
    this.signUpDate = new Date().toLocaleDateString("en-GB");
  },
  methods: {
    fetchCountryList: debounce(async function () {
      const countryList = await SolFileAPIInstance.getCountryListWithParams(
        this.countrySearch
      );
      this.countriesList = countryList && countryList.data;
    }, 300),

    fetchCityList: debounce(async function () {
      const params = {
        countryName: this.userInfo.birthCountry,
        cityName: this.citySearch,
      };
      const cityList = await SolFileAPIInstance.getCityListWithParams(params);
      this.citiesList =
        cityList &&
        cityList.data.map((item) => {
          return {
            ...item,
            cityFullName: `${item.cityName} (Latitude: ${item.lat}, Longitude: ${item.lng})`,
          };
        });
    }, 300),

    getSelectedCountry(value) {
      this.countrySearch = value;
      if (this.countrySearch) {
        this.userInfo.birthCity = null;
        this.fetchCountryList();
      }
    },

    getSelectedCity(value) {
      this.citySearch = value;
      this.citySearch && this.fetchCityList();
    },

    objectSanitizer(data) {
      return Object.entries(data).reduce((target, item) => {
        const [key, value] = item;
        if (value) {
          target[key] = typeof value === "string" ? value.trim() : value;
        }
        return target;
      }, {});
    },

    async handleFormSubmit() {
      if (this.formValidation()) {
        const token = await SolFileAPIInstance.handleGetNewUserToken({
          email: this.userInfo.email,
          password: this.userInfo.password,
        });
        if (token) {
          await SolFileAPIInstance.handleCreateUser(token.data.token, {
            ...this.objectSanitizer(this.userInfo),
            birthCity: this.userInfo.birthCity,
            birthCountry: this.userInfo.birthCountry,
            birthDate: `${this.dateTimeFields.date}T${this.dateTimeFields.time}:00`,
            language: this.userInfo.language,
            forcePremium: {
              active: this.forceSubscription === "Force Premium",
            endDate: this.forceSubscription === "Force Premium" ? this.endOfAccess : null,
            },
          })
        }
      }
    },

    handlePopupClose() {
      this.modalView = false;
    },

    handlePopupOpen() {
      this.modalView = true;
    },

    handleRedirect() {
      this.$router.push({
        path: `/individuals`,
      });
    },

    formValidation() {
      this.errorMessage = [];
      this.highlightingData = {
        firstName: false,
        lastName: false,
        passwordConfirm: false,
        password: false,
        cityOfBirth: false,
        countryOfBirth: false,
        email: false,
      };
      const { password, email, firstName, lastName, birthCity, birthCountry } =
        this.userInfo;

      const { date, time } = this.dateTimeFields;

      const { passwordConfirm } = this.validateFields;

      if (passwordConfirm !== password) {
        if (password.trim().length <= 6) {
          this.highlightingData.password = true;
          this.errorMessage.push("Please enter correct password");
        }
        this.errorMessage.push(
          "Password and confirm password fields should be the same"
        );
      }

      if (!email.trim()) {
        this.highlightingData.email = true;
        this.errorMessage.push("Please specify email address");
      }
      if (!firstName.trim()) {
        this.highlightingData.firstName = true;
        this.errorMessage.push("Please specify first name");
      }
      if (!lastName.trim()) {
        this.highlightingData.lastName = true;
        this.errorMessage.push("Please specify last name");
      }
      if (!password) {
        this.errorMessage.push("Please specify password");
      }
      if (!date) {
        this.errorMessage.push("Please specify date of birth");
      }
      if (!time) {
        this.errorMessage.push("Please specify time of birth");
      }
      if (!birthCity) {
        this.highlightingData.cityOfBirth = true;
        this.errorMessage.push("Please specify city of birth");
      }
      if (!birthCountry) {
        this.highlightingData.countryOfBirth = true;
        this.errorMessage.push("Please specify country of birth");
      }
      setTimeout(() => {
        this.errorMessage = [];
      }, 2000);
      return !this.errorMessage.length;
    },
  },
};
</script>

<style scoped lang="scss">
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

input {
  -webkit-appearance: none;
}

.row {
  margin: 0 -15px;
  display: flex;
  flex-wrap: wrap;
}

.col-6 {
  @media (min-width: 1199px) {
    flex: 0 50%;
    max-width: 50%;
  }

  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 15px;
  flex: 0 1 100%;
  max-width: 100%;

  &.buttons-wrapper {
    width: 100%;
    flex-direction: row;
    margin-left: auto;
    @media (max-width: 767px) {
      justify-content: center;
      margin-bottom: 10px;
    }
  }

  &.align-end {
    justify-content: flex-end;

    @media (max-width: 767px) {
      justify-content: space-between;
    }
  }
}

.create-page-wrapper {
  padding: 10px;

  .vue-select{
    margin-bottom: 20px;
  }

  input {
    box-sizing: border-box;
    display: block;
    padding: 15px;
    background-color: #fff !important;
    border: none;
    color: black;
    border-radius: 6px;
    width: 100%;
    margin: 0 auto 20px;

    &:focus {
      outline: none;
    }

    &.not-filled {
      border: 1px solid red;
    }
  }

  button,
  input[type="submit"] {
    box-sizing: border-box;
    padding: 15px;
    background-color: #fff;
    border: none;
    color: black;
    border-radius: 6px;
    width: 150px;
    margin: 0 10px 0 0;
    cursor: pointer;
    transition: ease all 0.4s;

    @media (max-width: 767px) {
      margin: 0;
    }

    &:focus {
      outline: none;
    }

    &:hover {
      color: white;
      background-color: #34558b !important;
    }

    &:last-child {
      margin: 0;
    }
  }

  .isRequired {
    .v-select.vs--single.vs--searchable,
    input {
      border: 1px solid red;
    }
  }
  input:disabled {
    background: #c4c4c4 !important;
    color: #333;
    cursor: not-allowed;
  }
  .check-subscription-btn {
    align-self: flex-end;
    width: 100%;
    max-width: 220px;
    text-align: center;
    position: relative;
    &.checking {
      pointer-events: none;
    }
    .sub-message {
      position: absolute;
      top: 110%;
      left: 5px;
      color: rgb(0, 216, 0);
      font-weight: 500;
      font-size: 14px;
      opacity: 0;
      transition: 0.3s ease-in;
      &.active {
        opacity: 1;
      }
    }
  }
}
</style>